import {useState, useRef, useMemo} from 'preact/hooks';
import classnames from 'classnames';
import {Swiper, SwiperSlide} from 'swiper/react';

import {useQfScreen} from '../comp/utils/hook';
import {practice} from '~/locals/qianfan/index';
import {useCptDuration} from '~/utils/trackCptDuration/hook';
import {applicationName} from '~/utils/trackCptDuration/constants';

const id = 'practiceForModel';
const moduleTitleClassName = 'qf-module-title';
const moduleDescClassName = 'qf-module-desc';
const moduleContentClassName = 'qf-module-content';
const Practice = () => {
    const itemsRef = useRef(null);
    const {navTitle, title, titleMobile, desc, descMobile, content} = practice;
    const [activeKey, setActiveKey] = useState('onlineEducation');
    const [activeSubKey, setActiveSubKey] = useState(0);
    const {screenWidth} = useQfScreen();

    useCptDuration(id, {
        name: `${applicationName}-${title}-停留时长`
    });

    const getMap = () => {
        if (!itemsRef.current) {
            itemsRef.current = new Map();
        }
        return itemsRef.current;
    };

    const handleNavClick = value => {
        setActiveKey(value);
        setActiveSubKey(0);
    };

    const llmSceneList = useMemo(() => {
        // 小屏幕展示全部场景
        const miniScreen = content[activeKey];
        // 大屏只展示一个，然后用activeSubKey进行切换
        const largerScreen = [content[activeKey][activeSubKey]?.content].filter(Boolean);

        // return screenWidth < 768 ? miniScreen : largerScreen || [content.onlineEducation[0].content];

        return miniScreen;
    }, [activeKey, activeSubKey, screenWidth]);

    // 轮播
    const swiperRef = useRef<Swiper>();
    const [slidePerView, setSlidePerView] = useState(1);

    const swiperDefaultConfig = useMemo(
        () => ({
            observer: true,
            observeParents: true,
            freeMode: false,
            centeredSlides: true,
            loop: true,
            // slidesPerGroup: 1,
            slidesPerView: 1.15,
            spaceBetween: 10,

            autoplay: {
                delay: 5000,
                disableOnInteraction: false
            },
            navigation: {
                clickable: true
            }
            // breakpoints: {
            //     375: {
            //         slidesPerView: 1.25,
            //         spaceBetween: 12
            //     },
            //     455: {
            //         slidesPerView: 1.1,
            //         spaceBetween: 12
            //     },
            //     768: {
            //         slidesPerView: 1.25,
            //         spaceBetween: 12
            //     }
            // }
        }),
        [slidePerView]
    );

    return (
        <div className="practice-for-model" id={id}>
            <div className="module-wrapper">
                <div className="module-container">
                    <h2 className={`title ${moduleTitleClassName}`}>{screenWidth < 600 ? titleMobile : title}</h2>
                    <p className={`module-desc no_rem_fs_16 ${moduleDescClassName}`}>
                        {screenWidth < 767 ? descMobile : desc}
                    </p>
                    <div className={`${moduleContentClassName}`}>
                        <ul className="segmented-group">
                            {navTitle.map((item, index) => (
                                <li
                                    className={classnames('segmented-group-item', {
                                        'segmented-group-item_active': activeKey === item.id
                                    })}
                                    key={item.id}
                                    ref={node => {
                                        const map = getMap();
                                        if (node) {
                                            map.set(item.id, node);
                                        } else {
                                            map.delete(item.id);
                                        }
                                    }}
                                    onClick={() => {
                                        handleNavClick(item.id);
                                    }}
                                >
                                    {item.name}
                                </li>
                            ))}
                        </ul>
                        <div className="tabs-content">
                            {/* 根据屏幕宽度条件渲染 */}
                            {screenWidth < 768 ? (
                                // 小于 768px 时使用 Swiper
                                <Swiper {...swiperDefaultConfig} ref={swiperRef} className={'scene-main'}>
                                    {llmSceneList.map((cont, index) => (
                                        <SwiperSlide key={cont.title}>
                                            <SceneContent cont={cont.content} idx={index} activeSubKey={activeSubKey} />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            ) : (
                                // 大于等于 768px 时直接渲染内容
                                <>
                                    {/* 场景侧边导航 */}
                                    <ul className="scene-nav">
                                        {content[activeKey].map((item, index) => (
                                            <li
                                                className={classnames('nav-item', {
                                                    'nav-item-active': activeSubKey === index
                                                })}
                                                key={item.id}
                                                onClick={() => {
                                                    setActiveSubKey(index);
                                                }}
                                            >
                                                {item.tit}
                                            </li>
                                        ))}
                                    </ul>
                                    {/* 场景内容 */}
                                    <div className="scene-main">
                                        {content[activeKey].map((cont, index) => (
                                            <SceneContent
                                                key={cont.title}
                                                cont={cont.content}
                                                idx={index}
                                                activeSubKey={activeSubKey}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SceneContent = ({cont, idx, activeSubKey}) => {
    const {screenWidth} = useQfScreen();
    const isMobile = screenWidth < 768;
    return (
        <div
            className={`scene-item ${isMobile ? '' : 'scene-item-pc'} ${
                idx === activeSubKey ? 'scene-item-active' : ''
            }`}
            style={{
                backgroundColor: '#f1f6fd',
                backgroundImage: isMobile
                    ? `linear-gradient(to right, #f1f6fd 0%, #f1f6fd calc(100% - 180px), transparent calc(100% - 180px), transparent 100%), url(${cont.sceneBg})`
                    : `linear-gradient(to right, #f1f6fd 0%, #f1f6fd 30%, transparent 30%, transparent 100%), url(${cont.sceneBg})`,
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'left top, right top',
                backgroundSize: `100% 100%, ${isMobile ? '180px' : '70% 100%'}`,
                borderRadius: '16px'
            }}
        >
            {/* logo 区域 */}
            <div className="scene-item-header">
                <div className="logo">
                    <img src={cont.icon} alt="logo" />
                </div>
                <div className="title">
                    <h4>{cont.tit}</h4>
                    <span className="tag">模型能力维度：{cont.tag}</span>
                </div>
            </div>
            {/* 场景介绍 */}
            <p className="scene-item-des no_rem_fs_16">场景介绍：{cont.des}</p>
            {/* 场景方案 */}
            <h5>{cont.t1}</h5>
            <div className="sub-tit1">
                {(cont?.d1 || []).map(i => (
                    <span className="sub-tit1_tag" key={i}>
                        {i}
                    </span>
                ))}
            </div>
            {/* 解决方案样板间 */}
            <h5>{cont.t2}</h5>
            <div className="sub-tit2">
                {(cont?.d2 || []).map(i => (
                    <div className="scene-scheme" key={i.p1}>
                        <span className="scene-scheme_title no_rem_fs_16">{i.p1}</span>
                        <span className="scene-scheme_link no_rem_fs_16">
                            <a href={i?.link} target="_blank" rel="noopener noreferrer">
                                {i.p2}
                            </a>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Practice;
